import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

export default function PaymentTrackingSection({ shipment, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Local state for editing
  const [localPaymentStatus, setLocalPaymentStatus] = useState(shipment?.payment_status || 'non_paye');
  const [localPaidAmount, setLocalPaidAmount] = useState(shipment?.paid_amount || 0);
  const [localPaymentMethod, setLocalPaymentMethod] = useState(shipment?.payment_method || '');
  const [localPaymentNotes, setLocalPaymentNotes] = useState(shipment?.payment_notes || '');

  // ✅ FIX: Calculate total from shipment_items sum minus discount_amount
  const calculateItemsTotal = () => {
    if (!shipment?.items || shipment?.items?.length === 0) return 0;
    return shipment?.items?.reduce((sum, item) => sum + parseFloat(item?.priceTotal || 0), 0);
  };

  const itemsTotal = calculateItemsTotal();
  const discountAmount = parseFloat(shipment?.discount_amount || 0);
  const totalAmount = Math.max(itemsTotal - discountAmount, 0);
  
  // ✅ FIX: Read paid_amount directly from shipment prop
  const paidAmount = parseFloat(shipment?.paid_amount || 0);
  const remainingAmount = Math.max(totalAmount - paidAmount, 0);

  // Reset local state when shipment changes
  useEffect(() => {
    setLocalPaymentStatus(shipment?.payment_status || 'non_paye');
    setLocalPaidAmount(shipment?.paid_amount || 0);
    setLocalPaymentMethod(shipment?.payment_method || '');
    setLocalPaymentNotes(shipment?.payment_notes || '');
  }, [shipment]);

  // Auto-adjust status based on amount changes
  const handleAmountChange = (newAmount) => {
    setLocalPaidAmount(newAmount);
    
    // Auto-calculate status
    if (newAmount <= 0) {
      setLocalPaymentStatus('non_paye');
    } else if (newAmount >= totalAmount) {
      setLocalPaymentStatus('paye');
    } else {
      setLocalPaymentStatus('partiel');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const { error } = await supabase
        ?.from('shipments')
        ?.update({
          paid_amount: localPaidAmount || 0,
          payment_status: localPaymentStatus,
          payment_method: localPaymentMethod || null,
          payment_notes: localPaymentNotes || null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', shipment?.id);

      if (error) throw error;

      alert('✓ Paiement mis à jour avec succès');
      setIsEditing(false);
      
      // Reload shipment data
      if (onUpdate) {
        await onUpdate();
      }
    } catch (err) {
      console.error('❌ Erreur mise à jour paiement:', err);
      alert(`Erreur: ${err?.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to original values
    setLocalPaymentStatus(shipment?.payment_status || 'non_paye');
    setLocalPaidAmount(shipment?.paid_amount || 0);
    setLocalPaymentMethod(shipment?.payment_method || '');
    setLocalPaymentNotes(shipment?.payment_notes || '');
    setIsEditing(false);
  };

  // ✅ FIX: Payment status badge with correct 'paye' mapping
  const getStatusBadge = (status) => {
    const badges = {
      'paye': { bg: 'bg-green-100', text: 'text-green-800', label: 'Payé' },
      'partiel': { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Partiel' },
      'non_paye': { bg: 'bg-red-100', text: 'text-red-800', label: 'Non payé' }
    };
    
    // ✅ Normalize status: handle 'paye', 'non_paye', 'partiel' and legacy values
    let normalizedStatus = status;
    if (status === 'paid' || status === 'paye') normalizedStatus = 'paye';
    else if (status === 'partial' || status === 'partiel') normalizedStatus = 'partiel';
    else if (!status || status === 'unpaid' || status === 'non_paye') normalizedStatus = 'non_paye';
    
    const badge = badges?.[normalizedStatus] || badges?.['non_paye'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${badge?.bg} ${badge?.text}`}>
        {badge?.label}
      </span>
    );
  };

  // ✅ FIX: Label "Total payé" when payment_status = 'paye', otherwise "Total à payer"
  const isPaid = shipment?.payment_status === 'paye';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
            <Icon name="CreditCard" size={24} className="text-emerald-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Suivi du paiement</h2>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Modifier
          </button>
        )}
      </div>
      {!isEditing ? (
        // Display mode
        (<div className="space-y-4">
          {/* Payment Summary */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Statut:</span>
              {getStatusBadge(shipment?.payment_status)}
            </div>

            {/* ✅ Show items subtotal and discount if discount exists */}
            {discountAmount > 0 && (
              <>
                <div className="flex justify-between py-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Sous-total articles:</span>
                  <span className="text-sm text-gray-700">
                    {itemsTotal?.toFixed(2)} €
                  </span>
                </div>
                <div className="flex justify-between py-2 border-t border-gray-200">
                  <span className="text-sm text-gray-600">Remise:</span>
                  <span className="text-sm font-medium text-red-600">
                    -{discountAmount?.toFixed(2)} €
                  </span>
                </div>
              </>
            )}
            
            <div className="flex justify-between py-2 border-t border-gray-200">
              {/* ✅ FIX: Label changes based on payment status */}
              <span className="text-sm text-gray-600">{isPaid ? 'Total payé:' : 'Total à payer:'}</span>
              <span className="text-sm font-semibold text-gray-900">
                {totalAmount?.toFixed(2)} €
              </span>
            </div>
            
            <div className="flex justify-between py-2 border-t border-gray-200">
              <span className="text-sm text-gray-600">Montant payé:</span>
              <span className="text-sm font-semibold text-emerald-600">
                {paidAmount?.toFixed(2)} €
              </span>
            </div>
            
            <div className="flex justify-between py-2 border-t border-gray-200">
              <span className="text-sm font-medium text-gray-900">Reste à payer:</span>
              <span className={`text-lg font-bold ${remainingAmount === 0 ? 'text-emerald-600' : 'text-gray-900'}`}>
                {remainingAmount?.toFixed(2)} €
              </span>
            </div>
          </div>
          {/* Payment Details */}
          {shipment?.payment_method && (
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-gray-600">Mode de paiement:</span>
              <span className="text-sm font-medium text-gray-900">{shipment?.payment_method}</span>
            </div>
          )}
          {shipment?.payment_notes && (
            <div className="py-2">
              <span className="text-sm text-gray-600 block mb-1">Notes:</span>
              <p className="text-sm text-gray-900">{shipment?.payment_notes}</p>
            </div>
          )}
        </div>)
      ) : (
        // Edit mode
        (<div className="space-y-4">
          {/* Payment Status Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut du paiement
            </label>
            <select
              value={localPaymentStatus}
              onChange={(e) => setLocalPaymentStatus(e?.target?.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="non_paye">Non payé</option>
              <option value="partiel">Partiel</option>
              <option value="paye">Payé</option>
            </select>
          </div>
          {/* Paid Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Montant déjà payé (€)
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={localPaidAmount || 0}
              onChange={(e) => handleAmountChange(Number(e?.target?.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Total à payer : {totalAmount?.toFixed(2)} € (articles: {itemsTotal?.toFixed(2)} € - remise: {discountAmount?.toFixed(2)} €)
            </p>
          </div>
          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mode de paiement
            </label>
            <input
              type="text"
              value={localPaymentMethod}
              onChange={(e) => setLocalPaymentMethod(e?.target?.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="Espèces, Virement, etc."
            />
          </div>
          {/* Payment Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={localPaymentNotes}
              onChange={(e) => setLocalPaymentNotes(e?.target?.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              placeholder="Informations complémentaires..."
            />
          </div>
          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
            >
              Annuler
            </button>
          </div>
        </div>)
      )}
    </div>
  );
}